import i18n from 'i18next';
import { actionCreators } from '../../store/actions';
import ReduxActions from '../../store/ReduxActions';
import {
  CompanyDetailsInterface,
  Franchise,
  ICompanyImpressum,
  ILegalDocument,
  InitialDataResponseInterface,
  LegalDocumentSlug,
} from '../exports/Interfaces';
import BackendApiClient from './BackendApiClient';
import { ERROR_CODES } from '../exports/Constants';
import { DataWrappedResponse, ErrorResponse, Response } from '../exports/Types';
import DomainResource from '../resources/DomainResource';
import AuthorizationTokenStorage from '../auth/AuthorizationTokenStorage';
import collect from 'collect.js';
import ImageResource from '../resources/ImageResource';
import WindowResource from '../resources/WindowResource';
import LocalizationManager from '../localization/LocalizationManager';

class InitialLoadingManager {
  public async getFranchiseData(hideLoading: boolean = false): Promise<DataWrappedResponse<Franchise>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    const request = await BackendApiClient.request({
      method: 'GET',
      url: '/franchises',
      params: DomainResource.getFranchiseResolverParams(),
    });

    if (!hideLoading) {
      this.toggleLoading(false);
    }

    const { response, success } = request;

    if (success) {
      ReduxActions.dispatch(actionCreators.initialData.setFranchise(response.data.data));
    }

    return request;
  }

  public async getFranchiseImpressums(hideLoading: boolean = true): Promise<DataWrappedResponse<ICompanyImpressum[]>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    const request = await BackendApiClient.request({
      method: 'GET',
      url: '/franchises/impressums',
      params: DomainResource.getFranchiseResolverParams(),
    });

    if (!hideLoading) {
      this.toggleLoading(false);
    }

    return request;
  }

  /**
   * One restaurant's legal document.
   *
   * `lang` is sent as i18next resolved it, region tag and all — the backend strips the
   * region itself, so this must not try to be clever about it.
   */
  public async getLegalDocument(
    slug: string,
    document: LegalDocumentSlug,
    lang?: string,
  ): Promise<DataWrappedResponse<ILegalDocument>> {
    return BackendApiClient.request({
      method: 'GET',
      url: `/companies/${slug}/legal-documents/${document}`,
      params: {
        ...DomainResource.getFranchiseResolverParams(),
        ...(lang ? { lang } : {}),
      },
    });
  }

  public async getDefaultCompanySlug(): Promise<null | string> {
    const { success, response } = await this.getFranchiseData(true);

    if (!success) {
      return null;
    }

    return collect(response.data.data?.companies).first()?.slug ?? null;
  }

  public async getCompanyData(
    slug: string = null,
    hideLoading: boolean = false,
    shouldRetry: boolean = true,
  ): Promise<Response<InitialDataResponseInterface>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    if (!slug) {
      slug = await this.getDefaultCompanySlug();
    }

    if (!slug) {
      return this.handleNoCompanyOnline(hideLoading);
    }

    const params = DomainResource.getFranchiseResolverParams();

    const request = await BackendApiClient.request({
      method: 'GET',
      url: `/companies/${slug}/data`,
      params,
    });
    if (!hideLoading) {
      this.toggleLoading(false);
    }

    const { response, success, status } = request;

    if (success) {
      this.saveDataRedux(response?.data);
    }

    if (status === 404) {
      if (slug !== null || !shouldRetry) {
        return this.handleCompanyNotFound(request);
      }

      return this.getCompanyData(await this.getDefaultCompanySlug(), hideLoading, false);
    }

    return request;
  }

  public async getInitialData(
    slug: string = null,
    hideLoading: boolean = false,
    shouldRetry: boolean = true,
  ): Promise<Response<InitialDataResponseInterface>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    if (!slug) {
      slug = await this.getDefaultCompanySlug();
    }

    if (!slug) {
      return this.handleNoCompanyOnline(hideLoading);
    }

    const params = DomainResource.getFranchiseResolverParams();

    const request = await BackendApiClient.request({
      method: 'GET',
      url: `/companies/${slug}/initial-data`,
      params,
    });
    if (!hideLoading) {
      this.toggleLoading(false);
    }

    const { response, success, status } = request;

    if (success) {
      this.saveDataRedux(response?.data);
    }

    if (status === 404) {
      if (slug !== null || !shouldRetry) {
        return this.handleCompanyNotFound(request);
      }

      return this.getInitialData(await this.getDefaultCompanySlug(), hideLoading, false);
    }

    return request;
  }

  public init(slug: string = null, hideLoading: boolean = false): Promise<Response<InitialDataResponseInterface>> {
    if (AuthorizationTokenStorage.getToken()) {
      return this.getInitialData(slug, hideLoading);
    }

    return this.getCompanyData(slug, hideLoading);
  }

  private saveDataRedux(response: InitialDataResponseInterface): void {
    this.applyCompanyLanguage(response.company);

    ImageResource.preloadImages(collect(response.company?.pictures).take(3).toArray()).finally(() => {
      ReduxActions.batchActions([
        actionCreators.initialData.setAllData(response),
        actionCreators.profile.setProfile(response?.profile),
        actionCreators.paymentOptions.setPaymentOptions(response?.payment_options),
      ]);
    });
  }

  /**
   * Starts the visit in the language the restaurant configured — but only as a
   * default. A customer who already has a language of their own keeps it: the
   * storefront hand-off cookie, an explicit ?lng=, or a choice they made on an
   * earlier visit all win here, and the header picker stays in charge afterwards.
   */
  private applyCompanyLanguage(company?: CompanyDetailsInterface): void {
    if (LocalizationManager.hasExplicitChoice() || !company?.language) {
      return;
    }

    if (i18n.language?.split(/[-_]/)[0] !== company.language) {
      i18n.changeLanguage(company.language);
    }
  }

  /** A 404 for a company that existed and was deleted: a stale link rather than a slug that should resolve. */
  public isDeletedCompany(request: Response<unknown>): boolean {
    return (
      !request.success &&
      (request as ErrorResponse<{ error_code?: string }>).response?.response?.data?.error_code ===
        ERROR_CODES.COMPANY_DELETED
    );
  }

  /** Keeps the 404's error, so callers can tell a deleted company (`company_deleted`) from an unknown slug. */
  private handleCompanyNotFound<T>(request: Response<T>): ErrorResponse<T> {
    ReduxActions.dispatch(
      actionCreators.initialData.updateCompany({
        slug: null,
      }),
    );

    if (!WindowResource.isNotFoundPage()) {
      WindowResource.replace('/404');
    }

    return {
      success: false,
      status: 404,
      response: (request as ErrorResponse<T>).response,
    };
  }

  /**
   * The franchise has no restaurant online yet (none, or none with a slug), so there is no company
   * to ask for. The 404 page tells the visitor so rather than requesting `/companies/null/data`.
   */
  private handleNoCompanyOnline<T>(hideLoading: boolean): ErrorResponse<T> {
    if (!hideLoading) {
      this.toggleLoading(false);
    }

    return this.handleCompanyNotFound<T>({ success: false, status: 404, response: null });
  }

  private toggleLoading(loading?: boolean) {
    ReduxActions.dispatch(actionCreators.initialData.toggleLoading(loading));
  }
}

export default new InitialLoadingManager();
